// THIS FILE IS COPIED FROM SRC/MIXINS TO FUNCTIONS/SRC/HELPERS. ONLY EDIT THIS FILE IN SRC/MIXINS.
// CHANGES IN FUNCTIONS/SRC/HELPERS WILL BE OVERWRITTEN.

import { RawStat } from "@/stores/stats";

export const constants = {
  CO2_WASTEWATER: 0.55, // kg/m3
  CO2_TAPWATER: 0.302, // kg/m3
  CO2_KWH: 0.33, // kg/kWh
  MICRO_PLASTICS_PER_WASH: 2.5, // g/wash
  ENERGY_PER_LITER: 0.0082, // kWh/L
  LITERS_PER_WASH: 60, // L/wash
  SYNTHETICS_RATIO: 0.65, // amount of plastic in the laundry as a percentage
  FILTRATION_RATIO: 0.9, // plastic capture of the filter as a percentage
};

// Calculate CO2 savings from water and return number in kg
export function calculateCO2Savings(waterSaved: number): number {
  // Convert liters to m3
  const waterSavedM3 = waterSaved / 1000;

  // Calculate CO2 savings from water (kg)
  const CO2water = waterSavedM3 * (constants.CO2_TAPWATER + constants.CO2_WASTEWATER);

  // Calculate CO2 savings from energy (kg)
  const CO2energy = calculateEnergySaved(waterSaved) * constants.CO2_KWH;

  // Total CO2 savings (kg)
  const CO2total = CO2water + CO2energy;

  // Return rounded to 2 decimal places
  return Number(CO2total.toFixed(2));
}

export function calculateMicroplastics(waterProcessed: number): number {
  // Calculate number of washes
  const numWashes = waterProcessed / constants.LITERS_PER_WASH;

  // Calculate microplastics saved (g) = NumWashes * MPPerWashConstant * SyntheticsRatio * FiltrationRatio
  const microplastics =
    numWashes *
    constants.MICRO_PLASTICS_PER_WASH *
    constants.FILTRATION_RATIO *
    constants.SYNTHETICS_RATIO;

  // Return rounded to 2 decimal places
  return Number(microplastics.toFixed(2));
}

export function calculateEnergySaved(waterSaved: number): number {
  // Returns kWh
  return waterSaved * constants.ENERGY_PER_LITER;
}

/* MB2 stat conversions */
export function MB2WaterSaved(stats: RawStat) {
  if (!stats?.greyTotOutNormalMode) return 0;
  return Number(((stats.greyTotOutNormalMode ?? 0) - (stats.coldInNormalMode ?? 0)).toFixed(2));
}

export function MB2WaterProcessed(stats: RawStat) {
  if (!stats?.greyTotOutNormalMode && !stats?.hotOutNormalMode) return 0;
  return Number(((stats.greyTotOutNormalMode ?? 0) + (stats.hotOutNormalMode ?? 0)).toFixed(2));
}

export function MB2WaterUsed(stats: RawStat) {
  return Number(MB2WaterProcessed(stats) - MB2WaterSaved(stats));
}

export function MB2RecyclingRate(stats: RawStat) {
  return Number(((MB2WaterSaved(stats) / MB2WaterProcessed(stats)) * 100).toFixed(2));
}

export function MB2NewRecyclingRate(stats: RawStat) {
  if (!stats.savedNormalMode) return 0;
  const total =
    (stats.hotOutNormalMode ?? 0) + (stats.coldInNormalMode ?? 0) + (stats.savedNormalMode ?? 0);

  return Number((((stats.savedNormalMode ?? 0) / total) * 100).toFixed(2));
}

export function MB2RecyclingFactor(stats: RawStat) {
  return Number((MB2WaterProcessed(stats) / MB2WaterUsed(stats)).toFixed(2));
}

export function MB2WMTotalConsumption(stats: RawStat): number {
  const greyTotOut = stats?.greyTotOut || 0;
  const greyTotOutIsNumber = isNumber(greyTotOut);
  const hotOut = stats?.hotOut || 0;
  const hotOutIsNumber = isNumber(hotOut);
  if (greyTotOutIsNumber && hotOutIsNumber) return greyTotOut + hotOut;

  if (!greyTotOutIsNumber && hotOutIsNumber) return hotOut;
  if (!hotOutIsNumber && greyTotOutIsNumber) return greyTotOut;

  return 0;
}

const isNumber = (maybeNumber: any): maybeNumber is Number => {
  return !(Number.isNaN(maybeNumber) || maybeNumber == null);
};
/**
 * Calculate the values between two stats
 *
 * @param latestStatInDateRange The latest stat within a date range
 * @param latestStatBeforeDateRange The latest stat just before the date range
 * @returns A calculated version of Stats subtracting specified keys
 */
export function MB2StatsDiff(
  latestStatInDateRange: RawStat,
  latestStatBeforeDateRange: RawStat,
): Partial<RawStat> {
  const keysToCalculate: Partial<keyof RawStat>[] = [
    "coldIn",
    "coldInNormalMode",
    "greyTotOut",
    "greyTotOutNormalMode",
    "greyWm2Out",
    "greyWm2OutNormalMode",
    "hotOut",
    "hotOutNormalMode",
    "saved",
    "savedNormalMode",
  ];
  const calculatedStats: Partial<RawStat> = {};

  keysToCalculate.forEach((key) => {
    const valueInRange = latestStatInDateRange?.[key];
    const valueBeforeRange = latestStatBeforeDateRange?.[key];
    let newCalc: number;
    if (isNumber(valueInRange) && isNumber(valueBeforeRange)) {
      newCalc = valueInRange - valueBeforeRange;
    } else if (isNumber(valueInRange) && !isNumber(valueBeforeRange)) {
      newCalc = valueInRange;
    } else {
      newCalc = 0;
    }
    (calculatedStats[key] as Number) = newCalc;
  });
  return calculatedStats;
}
